import React,  { useRef } from "react";
import { CopyBlock, dracula } from "react-code-blocks";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import headerImage from "../../assets/header5.jpg";
import w5_1 from "../../assets/w5_1.jpg";
import w5_10 from "../../assets/w5_10.jpg";
import w5_11 from "../../assets/w5_11.jpg";
import w5_12 from "../../assets/w5_12.jpg";
import w5_13 from "../../assets/w5_13.jpg";
import w5_14 from "../../assets/w5_14.jpg";
import w5_15 from "../../assets/w5_15.jpg";
import w5_16 from "../../assets/w5_16.jpg";
import w5_17 from "../../assets/w5_17.jpg";
import w5_19 from "../../assets/w5_19.jpg";
import w5_20 from "../../assets/w5_20.jpg";
import w5_28 from "../../assets/w5_28.jpg";
import w5_29 from "../../assets/w5_29.jpg";
import w5_30 from "../../assets/w5_30.jpg";
import w5_ from "../../assets/w5_.jpg";
import w5_50 from "../../assets/w5_50.jpg";
import w5_35_2 from "../../assets/w5_35_2.jpg";
import w5_51 from "../../assets/w5_51.jpg";
import ImageGallery from "../../components/ImageGallery";
import MediaGallery from "../../components/MediaGallery";
import ImageGallery2 from "../../components/ImageGallery2";
import ImageGallery3 from "../../components/ImageGallery3";
import ImageGallery4 from "../../components/ImageGallery4";

const Assignment6 = () => {
  const DDDesign = useRef(null);
  const printing = useRef(null);
  const scan = useRef(null);
  const groupAssignment = useRef(null);
  const downloads = useRef(null);

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth" });
  };


    
  return (

    <div className="container mx-auto my-8 px-5 max-w-3xl bg-gray-100 p-8 rounded-lg shadow-md">
      {/* Header */}
      <div className="relative mb-8">
        <img
          src={headerImage}
          alt="Header"
          className="w-full h-64 object-cover rounded-lg shadow-md  object-cover filter grayscale brightness-75"
        />
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4 rounded-b-lg">
          <h1 className="text-4xl font-bold text-bg-secondary"  style={{ textShadow: '2px 2px 4px rgba(0, 0, 0, 1)' }}>5. Week - 3D Scanning and Printing</h1>
          <p className="text-gray-300 mt-2">Published on: <span className="font-semibold">February 24, 2025</span></p>
          <div className="mt-4 flex space-x-4">
         {/* Navigation Links */}
      <div className="mt-4 flex space-x-4">
        <button onClick={() => scrollToSection(DDDesign)} className="text-white bg-secondary px-3 py-1 rounded hover:bg-secondary">
         3D Design
        </button>
        <button onClick={() => scrollToSection(printing)} className="text-white bg-secondary px-3 py-1 rounded hover:bg-secondary">
          Printing
        </button>
        <button onClick={() => scrollToSection(scan)} className="text-white bg-secondary px-3 py-1 rounded hover:bg-secondary">
          Scanning
        </button>
        <button onClick={() => scrollToSection(groupAssignment)} className="text-white bg-secondary px-3 py-1 rounded hover:bg-secondary">
          Group Assignment
        </button>
        <button onClick={() => scrollToSection(downloads)} className="text-white bg-secondary px-3 py-1 rounded hover:bg-secondary">
          Downloads
        </button>
        </div>
        </div>
        </div>
        </div>


        <p className="text-justify text-gray-700 mb-6">
        For this week's assignment, I was contemplating what to design. The goal was to create something that wouldn’t be easily manufacturable using traditional subtractive methods. My initial idea was a simple ball trapped inside a cube, but it didn’t feel challenging or exciting enough to pursue.
      </p>
      <p className="text-justify text-gray-700 mb-6">
      I then started thinking about fascinating structures that have always intrigued me but that I never had the time to explore—mathematically designed 3D-printed objects. Curious about what was possible, I conducted some research and came across various types of mathematical and organic structures:
      <ul className="pl-5 list-disc">
  <li><strong>Gyroid Structures</strong> – Lightweight yet strong, ideal for vases or lampshades.</li>
  <li><strong>Voronoi Patterns</strong> – Organic, nature-inspired designs perfect for decorative holders or covers.</li>
  <li><strong>Möbius Strips</strong> – A continuous, one-sided surface, great for artistic sculptures, pen holders, or jewelry.</li>
  <li><strong>Sierpinski Triangles & Fractals</strong> – Intricate, self-repeating patterns that create mesmerizing forms.</li>
  <li><strong>Hyperboloid Structures</strong> – Common in architecture, but also useful for aesthetic stands and supports.</li>
</ul>
<p className="text-justify text-gray-700 mb-6">
Since I’ve always been fascinated by fractals, I decided to design something inspired by these unique geometric formations.
      </p>
        {/* 3D Design Section*/}
        <div ref={DDDesign}></div>
        <h2 className="text-3xl font-bold text-left mb-6">3D Design</h2>
      <p className="text-justify text-gray-700 mb-6">
      While researching, I discovered a powerful tool that simplifies the transformation of mathematical formulas into 3D structures: OpenSCAD (<a href="https://openscad.org/" style={{ color: "purple" }}>https://openscad.org/</a>). This open-source software allows for parametric modeling using code, making it an ideal choice for experimenting with algorithmically generated forms.
      </p>
      <img src={w5_1} alt="OpenSCAD" className="my-4 mx-auto rounded shadow-md" />
      <p className="text-justify text-gray-700 mb-6">
      The concept of programming objects instead of designing them manually was entirely new to me. At first, I experimented with basic functions in OpenSCAD to understand how primitive forms are created using code.
      </p>
      <p className="text-justify text-gray-700 mb-6"><strong>Basic Shapes</strong></p>

<p className="text-justify text-gray-700 mb-6">
OpenSCAD provides simple functions to generate fundamental 3D shapes:
      </p>
      <CopyBlock
            text={`cube([10, 10, 10]);  // Creates a cube with dimensions 10x10x10 units
sphere(10);          // Creates a sphere with a radius of 10 units
cylinder(h=20, r=5); // Creates a cylinder with height 20 and radius 5 units
`}
            language="C"
            showLineNumbers={false}
            theme={dracula}
            codeBlock
          />

      <p className="text-justify text-gray-700 mb-6">
      Each function defines a shape using parametric values, meaning that their size can be easily adjusted by changing the input numbers. This flexibility is something that traditional CAD software often lacks.
      </p>
      <p className="text-justify text-gray-700 mb-6"><strong>Transformations</strong></p>

      <CopyBlock
            text={`translate([10, 0, 0]) cube(10);  // Moves the cube 10 units along the X-axis
rotate([0, 0, 45]) cube(10);    // Rotates the cube 45 degrees around the Z-axis
scale([2, 1, 1]) sphere(5);     // Scales only in the X direction, making the sphere elliptical
`}
            language="C"
            showLineNumbers={false}
            theme={dracula}
            codeBlock
          />

      <p className="text-justify text-gray-700 mb-6">
      With these transformations, I was able to manipulate objects dynamically instead of adjusting them manually.
      </p>
      <h3 className="text-2xl font-bold text-left mb-6">Diving into Fractal Designs</h3>

      <p className="text-justify text-gray-700 mb-6">
      After experimenting with basic transformations, I finally wanted to try out to design fractals — geometric patterns that repeat infinitely at different scales. I started implementing some of the most famous fractal structures in OpenSCAD.
      </p>
      
      <p className="text-justify text-gray-700 mb-6"><strong>Menger Sponge</strong></p>
      <p className="text-justify text-gray-700 mb-6">
  The <strong>Menger Sponge</strong> is a well-known fractal cube that consists of smaller cubes arranged recursively. It is generated by continuously subdividing a cube and removing specific sections at each level.
</p>

<ul className="list-disc ml-6 text-gray-700 mb-6">
  <li>The function <strong>recursively divides the cube into smaller cubes</strong>.</li>
  <li>Some cubes are <strong>removed based on their position</strong>, creating the <strong>fractal effect</strong>.</li>
  <li>The recursion stops when <strong>level = 0</strong>, meaning no further subdivisions occur.</li>
</ul>

<p className="text-justify text-gray-700 mb-6">
  This structure is particularly fascinating because it visually demonstrates <strong>self-similarity</strong>, a key feature of fractals.
</p>
<p className="text-justify text-gray-700 mb-6"><strong>Fractal Tree Patterns</strong></p>
<p className="text-justify text-gray-700 mb-6">
  Another type of fractal I explored was <strong>tree fractals</strong>, which are commonly used to simulate <strong>natural branching structures</strong> like trees, blood vessels, or river systems.
</p>

<p className="text-justify text-gray-700 mb-6">
  <strong>How a Fractal Tree is Constructed:</strong>
</p>

<ul className="pl-5 list-disc">
  <li>Start with <strong>a vertical trunk</strong>.</li>
  <li>At each step, <strong>create two (or more) branches</strong> that extend from the previous segment.</li>
  <li><strong>Rotate each branch</strong> slightly to create a natural shape.</li>
  <li><strong>Reduce the size</strong> of new branches in each iteration to maintain proportionality.</li>
  <li><strong>Stop recursion</strong> when the branches become too small to be meaningful.</li>
</ul>


<p className="text-justify text-gray-700 mb-6">
  <strong>Other Fascinating Fractal Designs</strong><br></br>
  Beyond the Menger Sponge and fractal trees, I also experimented with:
</p>

<ul className="list-disc ml-6 text-gray-700 mb-6">
  <li><strong>Koch Snowflake</strong> – A fractal curve that starts as a triangle and develops into a highly detailed shape through recursion.</li>
  <li><strong>Fractal Sphere</strong> – A sphere divided into progressively smaller segments to create an intricate pattern.</li>
  <li><strong>Spiral Structures</strong> –  Spiral structures created with the help of mathematical formulas.</li>
  <li><strong>Spiral built from Menger Sponges</strong> – A 3D model combining recursion and rotation</li>
</ul>

<p className="text-justify text-gray-700 mb-6">
  Exploring these mathematical structures gave me a deeper appreciation for the power of <strong>generative design</strong>. Instead of manually sculpting objects, I was able to <strong>define rules</strong> that would produce intricate patterns with just a few lines of code. This approach allows for endless experimentation and opens up new possibilities for <strong>complex, algorithmically generated models</strong>.
</p>
<p className="text-justify text-gray-700 mb-6">My experiments with fractal design can be viewed in the following image gallery.</p>

      <ImageGallery />

     </p>


      <p className="text-justify text-gray-700 mb-6">

      Originally, I wanted to 3D print the spiral made of Menger sponges as a jewelry pendant for this week's assignment. However, after discussing it, concerns were raised that it could also be manufactured using traditional methods. Therefore, I started looking for alternative possibilities. 
</p>
<h3 className="text-2xl font-bold text-left mb-6">Voronoi Patterns</h3>
<p className="text-justify text-gray-700 mb-6">
Next, I looked for tools to create 3D models with Voronoi patterns. During my research, I came across the website <a href="https://www.voronator.com/" style={{ color: "purple" }}>Voronator</a> . This platform allows users to upload 3D models, which are then transformed into Voronoi-patterned structures.
      </p>
      <img src={w5_11} alt="OpenSCAD" className="my-4 mx-auto rounded shadow-md" />
      <img src={w5_12} alt="Cylinder" className="my-4 mx-auto rounded shadow-md" />
      <img src={w5_10} alt="Sphere" className="my-4 mx-auto rounded shadow-md" />
      <p className="text-justify text-gray-700 mb-6">
      After processing my models through the Voronator tool, they transformed into the following structures with a Voronoi-patterned surface. The once solid objects now featured a lightweight, organic design while maintaining their original shape.
      
      </p>
      <img src={w5_16} alt="Sphere after using Voronator" className="my-4 mx-auto rounded shadow-md" />
      <img src={w5_17} alt="Cylinder after using Voronator" className="my-4 mx-auto rounded shadow-md" />
      <p className="text-justify text-gray-700 mb-6">
      Some might argue that these forms could still be manufactured using traditional fabrication techniques. To make my design truly exclusive to additive manufacturing, I decided to embed a freely movable sphere inside the Voronoi-patterned sphere. Objects within objects can only be produced using additive manufacturing methods, making this design a perfect fit for 3D printing.

      </p>

      <img src={w5_15} alt="Cylinder" className="my-4 mx-auto rounded shadow-md" />


        {/* 3D Printing Section*/}
        <div ref={printing}></div>
        <h2 className="text-3xl font-bold text-left mb-6">3D Printing</h2>
        <p className="text-justify text-gray-700 mb-6">
  For the fabrication of my Voronoi tealight holder, I used the <strong>Bambu Lab X1 Carbon</strong>, a highly popular and reliable 3D printer available in our FabLab. This printer is known for its high-speed performance, precision, and multi-material capabilities, making it a great choice for complex designs like the Voronoi pattern.
</p>

<p className="text-justify text-gray-700 mb-6">
  To ensure a smooth and high-quality print, I chose <strong>PLA filament</strong>, which is an excellent material for decorative and functional prints due to its ease of use, minimal warping, and environmentally friendly properties. The printer’s fine layer resolution and stable build platform allowed for the intricate Voronoi pattern to be printed without support structures.
</p>
      <p className="text-justify text-gray-700 mb-6">
  My goal was to transform the cylindrical Voronoi design into a functional tealight holder. For this purpose, the top part needed to be open.
</p>

<p className="text-justify text-gray-700 mb-6">
  To achieve this, I used a slicing tool to remove the upper section of the cylinder. This simple modification ensured that the tealight could be easily placed inside and allowed the light to shine through.
</p>
      <img src={w5_13} alt="Cylinder" className="my-4 mx-auto rounded shadow-md" />
      <img src={w5_14} alt="Cylinder" className="my-4 mx-auto rounded shadow-md" />

      <p className="text-justify text-gray-700 mb-6">
  Since I had previously conducted an <strong>Overhang 3D Printer Test</strong> with the Bambu Lab X1 Carbon to determine its design rules and limitations for the Group Assignment, I was confident that this printer could handle the structures without support issues. 
</p>

<p className="text-justify text-gray-700 mb-6">
  The test helped me understand the printer’s capabilities regarding overhang angles, bridging, and fine details, ensuring that my model would be printed successfully. With this knowledge, I proceeded to print my design, hoping that the Bamboo Lab X1 Carbon’s precise calibration and cooling system would allow for clean overhangs and sharp details.
</p>
<img src={w5_19} alt="Cylinder" className="my-4 mx-auto rounded shadow-md" />
<p className="text-justify text-gray-700 mb-6">
  After some time filled with anticipation, wondering whether the prints would turn out successfully, I was thrilled to see that the models were printed without any issues! </p>
  <MediaGallery />
            <p> <br></br></p>
  <p className="text-justify text-gray-700 mb-6">Here are the photos of the final prints. I am happy with the results. Only the print of the menger sponge spiral failed because of the support filament. </p>
  <ImageGallery2/>


        {/* 3D Printing Section*/}
        <div ref={scan}></div>
        <h2 className="text-3xl font-bold text-left mb-6">3D Scanning</h2>
        <p className="text-justify text-gray-700 mb-6">
          The second task for this week's assignment was to 3D scan an object and optionally print it. My first idea was to scan my phone to create a custom phone case using TPU filament.
      </p>
      <p className="text-justify text-gray-700 mb-6">
      I used the EinScan X scanner for the task. Before scanning, I sprayed the glossy and black surfaces of the phone with chalk paint to improve scan accuracy, as these surfaces are typically difficult for the scanner to capture.
      </p>
      <p className="text-justify text-gray-700 mb-6">
      To begin, I placed the phone on the turntable and selected the "Turntable" mode in the scanner software. After the initial scan, I performed additional scans using the "Geometry" setting. I attempted multiple scans, but each time I encountered layer shifts between the scans, and some areas remained incomplete, despite the chalk spray treatment.
      </p>
      <img src={w5_35_2} alt="Turntable Selection" className="my-4 mx-auto rounded shadow-md"/>
      <p className="text-justify text-gray-700 mb-6">
      In a further attempt, I decided to cover the phone with masking tape, as it has a light, matte surface that should be easier for the scanner to detect. Unfortunately, this method also did not produce the desired results.
      </p>
      <ImageGallery3/>
      <p className="text-justify text-gray-700 mb-6">
      Since this project had already taken several hours and the software had crashed multiple times, I decided to scan a different object. I chose a hand-modeled turtle as my new subject.
      </p>

      <ImageGallery4/>

      <p className="text-justify text-gray-700 mb-6">
      Due to its matte surface texture and the fact that it was not a flat object, the turtle was easier to scan. I first performed a scan of the top side and removed any unwanted points in the software. Then, I scanned the underside of the turtle and aligned the two project files. It took several attempts to figure out that I could use the Shift key to mark points on the model, which helped in aligning both sides. This allowed me to successfully create a complete 3D model of the turtle.
      </p>
      <p className="text-justify text-gray-700 mb-6">
      To save the scan I clicked "Global Optimization" and waited for some minutes. Then I used the tool "Mesh Optimization" and "Simplification" to get a small STL file. I included the file in the download section.
      </p>
      <img src={w5_50} alt="Mesh Optimization" className="my-4 mx-auto rounded shadow-md" />
      <img src={w5_51} alt="Simplification" className="my-4 mx-auto rounded shadow-md" />
        {/* Group Assignment*/}
        <div ref={groupAssignment}></div>
        <h2 className="text-3xl font-bold text-left mb-6">Reflection of the Group Assignment</h2>
        <p className="text-justify text-gray-700 mt-4">
        I am now going to reflect my findings of the Group Assignment. A detailed documentation of the group assignment including tests on various printers can be found here: 
  </p>
        
        <a href="https://fabacademy.org/2025/labs/bottrop/Assignments/week05/" style={{ color: "purple" }}>Link to the Group Assignment.</a>
        <h2 className="text-xl font-bold text-left mb-4">Bambu Lab X1 Carbon - Key Specifications</h2>
  <p className="text-justify text-gray-700 mb-4">
    The <strong>Bambu Lab X1 Carbon (X1C)</strong> is a high-performance <strong>Core XY 3D printer</strong> known for its <strong>speed, precision, and intelligent features</strong>. Below are some of its key specifications:
  </p>
  <ul className="list-disc ml-8 text-gray-700">
    <li><strong>Core XY Motion System</strong> – Enables <strong>high-speed printing</strong> with up to 20,000 mm/s² acceleration, ensuring fast and precise movement.</li>
    <li><strong>Multi-Color Printing</strong> – Supports up to <strong>16 colors</strong> when used with the Automatic Material System (AMS), allowing for complex, multi-material prints.</li>
    <li><strong>Advanced Auto Bed Leveling (ABL)</strong> – Equipped with dual auto bed leveling, ensuring a perfectly calibrated print surface before every print.</li>
    <li><strong>High-Resolution Lidar Sensor</strong> – Features a 7μm lidar resolution, enabling precise first-layer calibration and improved print accuracy.</li>
    <li><strong>AI-Powered Error Detection</strong> – Includes spaghetti failure detection, helping to identify and pause failed prints automatically.</li>
    <li><strong>First-Layer Inspection</strong> – Uses AI to inspect the first layer, ensuring proper adhesion and print quality before continuing the print job.</li>
  </ul>
  <p className="text-justify text-gray-700 mt-4">
    These features make the <strong>Bambu Lab X1 Carbon</strong> a powerful and reliable choice for high-speed, high-quality, and multi-color 3D printing, making it particularly valuable in FabLabs and professional prototyping environments.
  </p>
  <h2 className="text-xl font-bold text-left mb-4">Design Rules and Testing</h2>

  <p className="text-justify text-gray-700 mb-4">
    When designing objects for 3D printing, it is essential to follow specific guidelines to ensure successful prints without structural failures or print defects.
     Two critical aspects to test are clearance for moving parts and overhang limitations.
  </p>
  <img src={w5_28} alt="Design Rules" className="my-4 mx-auto rounded shadow-md" />

  <h3 className="text-xl font-bold text-gray-700 mt-6 mb-2">Clearance Test</h3>
  <p className="text-justify text-gray-700 mb-4">
    Clearance refers to the gap between two moving parts in a 3D-printed object. If the clearance is too small, parts might fuse together due to thermal expansion or inaccuracies in the printing process. A clearance test helps determine the **minimum gap required** for parts to move freely.
  </p>
  <img src={w5_30} alt="Clearance Test" className="my-4 mx-auto rounded shadow-md" />
  <h3 className="text-xl font-bold text-gray-700 mt-6 mb-2">Overhang Test</h3>
  <p className="text-justify text-gray-700 mb-4">
    Overhangs refer to parts of a model that extend outward without direct support beneath them. Most FDM 3D printers have a limitation where overhangs beyond a certain angle will sag or fail due to gravity. 
    An overhang test helps determine the maximum printable angle without support structures.
  </p>
  <img src={w5_29} alt="Overhang Test" className="my-4 mx-auto rounded shadow-md" />
  <p className="text-justify text-gray-700 mt-4">
  During the tests with the Bambu Lab X1 Carbon, we observed exceptional overhang performance— even at an 80° angle, no additional support structures were required. 
  Additionally, all hinges and moving parts in our clearance test functioned smoothly, demonstrating the printer’s high precision and reliability in handling complex geometries.
</p>

  <h3 className="text-2xl font-bold mt-6 mb-2">Learning Takeaways</h3>
  <ul className="list-disc ml-8 text-gray-700">
    <li>Clearance is essential for moving parts and assembly fit.</li>
    <li>Overhang testing helps define angle limits before requiring supports.</li>
    <li>Bridging tests can also be useful to see how well the printer handles gaps between two points.</li>
    <li>Each 3D printer and filament type behaves differently</li>
  </ul>

  <p className="text-justify text-gray-700 mt-4">
    Understanding and applying these design rules ensures higher success rates in 3D printing and reduces failed prints, warping, and material waste.
     Running clearance and overhang tests is an essential step for achieving high-quality prints with optimized settings.
  </p>

  <h2 className="text-3xl font-bold text-left mb-6 mt-2">Downloads</h2>
  <div ref={downloads}></div>
  <p className="text-justify text-gray-700 mt-4">
You can download my design files for 3D printing here: 

<a
    style={{ display: "block" }}
    href="https://fabacademy.org/2025/labs/bottrop/students/annasophia-brokuf/files/Sphere.3mf"
    download
  >
    📎 Download Sphere
  </a>
  <a
    style={{ display: "block" }}
    href="https://fabacademy.org/2025/labs/bottrop/students/annasophia-brokuf/files/Sphere2.3mf"
    download
  >
    📎 Download Sphere 2
  </a>
  <a
    style={{ display: "block" }}
    href="https://fabacademy.org/2025/labs/bottrop/students/annasophia-brokuf/files/cylinder_voro_simplified.3mf"
    download
  >
    📎 Download Cylinder
  </a>
  <a
    style={{display: "block" }}
    href="https://fabacademy.org/2025/labs/bottrop/students/annasophia-brokuf/files/Spyral_Menger_Sponge.stl"
    download
  >
    📎 Download Spiral Menger Sponge
  </a>
  <a
    style={{display: "block" }}
    href="https://fabacademy.org/2025/labs/bottrop/students/annasophia-brokuf/files/turtle_small.stl"
    download
  >
    📎 Download 3D Scan Turtle
  </a>
  </p>


        </div>



  );
};

export default Assignment6;
